/**
 * 🛡️ G2GENTE - Painel de Vagas com Filtros Avançados
 * Lista de vagas, filtros por cargo/local/contrato/inclusão
 */
import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { jobsAPI, candidatesAPI, matchesAPI } from '../../lib/api';
import { MatchBadge, useMatches } from '../../components/match/MatchBadge';
import { Card, CardContent } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Badge } from '../../components/ui/badge';
import { 
  Search, 
  MapPin, 
  Building2, 
  ArrowLeft,
  Loader2,
  Briefcase,
  Star,
  Filter,
  X,
  Heart,
  Bookmark,
  BookmarkCheck,
  Users
} from 'lucide-react';
import { toast } from 'sonner';

const TIPOS_CONTRATO = ['CLT', 'PJ', 'Estágio', 'Temporário', 'Freelancer'];

export default function MobileJobs() {
  const navigate = useNavigate();
  const [jobs, setJobs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [applying, setApplying] = useState(null);
  const [savedJobs, setSavedJobs] = useState([]);
  const [savingJob, setSavingJob] = useState(null);
  const [isOpenToOpportunities, setIsOpenToOpportunities] = useState(true);
  
  // Filtros
  const [showFilters, setShowFilters] = useState(false);
  const [filters, setFilters] = useState({
    cargo: '',
    localizacao: '',
    tipo_contrato: '',
    inclusao: false
  });
  
  // Lista única de localizações e cargos para filtro
  const [locations, setLocations] = useState([]);
  const [cargos, setCargos] = useState([]);

  useEffect(() => {
    checkOpenToOpportunities();
    fetchSavedJobs();
  }, []);

  const checkOpenToOpportunities = async () => {
    try {
      const response = await candidatesAPI.getMe();
      const isOpen = true;
      setIsOpenToOpportunities(isOpen);
      if (isOpen) {
        fetchJobs();
      } else {
        setLoading(false);
      }
    } catch (error) {
      fetchJobs(); // Fallback to showing jobs
    }
  };

  const fetchJobs = async () => {
    try {
      const response = await jobsAPI.getPublic();
      setJobs(response.data);
      
      // Extract unique locations and titles for filters
      const uniqueLocations = [...new Set(response.data.map(j => j.localizacao))];
      const uniqueCargos = [...new Set(response.data.map(j => j.titulo.split(' ')[0]))];
      setLocations(uniqueLocations);
      setCargos(uniqueCargos);
    } catch (error) {
      toast.error('Erro ao carregar vagas');
    } finally {
      setLoading(false);
    }
  };

  const fetchSavedJobs = async () => {
    try {
      const response = await candidatesAPI.getSavedJobs();
      setSavedJobs(response.data?.map(j => j.job_id) || []);
    } catch (error) {
      // Silently fail if endpoint doesn't exist yet
      console.log('Saved jobs not available');
    }
  };

  const handleApply = async (jobId, e) => {
    e.stopPropagation();
    setApplying(jobId);
    try {
      await jobsAPI.apply(jobId);
      toast.success('Candidatura enviada! 🎉');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erro ao candidatar');
    } finally {
      setApplying(null);
    }
  };

  const handleSaveJob = async (jobId, e) => {
    e.stopPropagation();
    setSavingJob(jobId);
    try {
      if (savedJobs.includes(jobId)) {
        await candidatesAPI.unsaveJob(jobId);
        setSavedJobs(savedJobs.filter(id => id !== jobId));
        toast.success('Vaga removida dos salvos');
      } else {
        await candidatesAPI.saveJob(jobId);
        setSavedJobs([...savedJobs, jobId]);
        toast.success('Vaga salva!');
      }
    } catch (error) {
      toast.error('Erro ao salvar vaga');
    } finally {
      setSavingJob(null);
    }
  };

  const clearFilters = () => {
    setFilters({
      cargo: '',
      localizacao: '',
      tipo_contrato: '',
      inclusao: false
    });
  };

  const activeFiltersCount = Object.values(filters).filter(v => v && v !== false).length;

  const filteredJobs = jobs.filter(job => {
    // Search text
    const searchMatch = !search || 
      job.titulo.toLowerCase().includes(search.toLowerCase()) ||
      job.localizacao.toLowerCase().includes(search.toLowerCase()) ||
      job.company_name?.toLowerCase().includes(search.toLowerCase());
    
    // Filters
    const cargoMatch = !filters.cargo || job.titulo.toLowerCase().includes(filters.cargo.toLowerCase());
    const locMatch = !filters.localizacao || job.localizacao === filters.localizacao;
    const contratoMatch = !filters.tipo_contrato || job.tipo_contrato === filters.tipo_contrato;
    const inclusaoMatch = !filters.inclusao || job.inclusao_pcd || job.inclusao_50_plus;
    
    return searchMatch && cargoMatch && locMatch && contratoMatch && inclusaoMatch;
  });
  const matchIds = filteredJobs.slice(0, 10).map((j) => j.id);
  const myMatches = useMatches(() => matchesAPI.mine(matchIds), matchIds.length ? matchIds.join(',') : null);

  return (
    <div className="min-h-screen bg-slate-50 pb-6" data-testid="mobile-jobs">
      {/* Header */}
      <div className="bg-white sticky top-0 z-10 border-b border-slate-200">
        <div className="flex items-center gap-3 p-4">
          <button onClick={() => navigate('/m')} className="text-slate-600">
            <ArrowLeft className="w-6 h-6" />
          </button>
          <h1 className="text-lg font-bold text-slate-900 flex-1">Vagas</h1>
          {isOpenToOpportunities && (
            <button 
              onClick={() => setShowFilters(!showFilters)}
              className={`relative p-2 rounded-lg ${showFilters ? 'bg-cyan-100 text-cyan-600' : 'text-slate-600'}`}
            >
              <Filter className="w-5 h-5" />
              {activeFiltersCount > 0 && (
                <span className="absolute -top-1 -right-1 w-5 h-5 bg-cyan-600 text-white text-xs rounded-full flex items-center justify-center">
                  {activeFiltersCount}
                </span>
              )}
            </button>
          )}
        </div>
        
        {/* Search */}
        <div className="px-4 pb-3">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-slate-400 w-5 h-5" />
            <Input
              type="text"
              placeholder="Buscar vagas..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="pl-10 bg-slate-100 border-0"
              data-testid="mobile-jobs-search"
            />
          </div>
        </div>

        {/* Filters Panel */}
        {showFilters && (
          <div className="px-4 pb-4 border-t border-slate-100 pt-3 bg-slate-50">
            <div className="flex items-center justify-between mb-3">
              <span className="text-sm font-medium text-slate-700">Filtros</span>
              {activeFiltersCount > 0 && (
                <button 
                  onClick={clearFilters}
                  className="text-xs text-cyan-600 flex items-center gap-1"
                >
                  <X className="w-3 h-3" /> Limpar
                </button>
              )}
            </div>
            
            {/* Cargo Filter */}
            <div className="mb-3">
              <label className="text-xs text-slate-500 mb-1 block">Cargo</label>
              <Input
                placeholder="Ex: Analista, Desenvolvedor..."
                value={filters.cargo}
                onChange={(e) => setFilters({...filters, cargo: e.target.value})}
                className="h-9 text-sm"
              />
            </div>
            
            {/* Localização Filter */}
            <div className="mb-3">
              <label className="text-xs text-slate-500 mb-1 block">Localização</label>
              <select
                value={filters.localizacao}
                onChange={(e) => setFilters({...filters, localizacao: e.target.value})}
                className="w-full h-9 text-sm rounded-md border border-input bg-background px-3"
              >
                <option value="">Todas</option>
                {locations.map(loc => (
                  <option key={loc} value={loc}>{loc}</option>
                ))}
              </select>
            </div>
            
            {/* Tipo Contrato Filter */}
            <div className="mb-3">
              <label className="text-xs text-slate-500 mb-1 block">Tipo de Contrato</label>
              <div className="flex flex-wrap gap-2">
                {TIPOS_CONTRATO.map(tipo => (
                  <Badge
                    key={tipo}
                    variant={filters.tipo_contrato === tipo ? "default" : "outline"}
                    className={`cursor-pointer text-xs ${
                      filters.tipo_contrato === tipo 
                        ? 'bg-cyan-600 hover:bg-cyan-700' 
                        : 'hover:bg-slate-100'
                    }`}
                    onClick={() => setFilters({
                      ...filters, 
                      tipo_contrato: filters.tipo_contrato === tipo ? '' : tipo
                    })}
                  >
                    {tipo}
                  </Badge>
                ))}
              </div>
            </div>
            
            {/* Inclusão Filter */}
            <div className="flex items-center gap-2">
              <input
                type="checkbox"
                id="inclusao-filter"
                checked={filters.inclusao}
                onChange={(e) => setFilters({...filters, inclusao: e.target.checked})}
                className="w-4 h-4 rounded border-slate-300 text-cyan-600 focus:ring-cyan-500"
              />
              <label htmlFor="inclusao-filter" className="text-sm text-slate-700 flex items-center gap-1">
                <Heart className="w-4 h-4 text-purple-500" />
                Vagas com políticas de inclusão
              </label>
            </div>
          </div>
        )}
      </div>

      {/* Content */}
      <div className="p-4">
        {/* Not Open to Opportunities Message */}
        {!isOpenToOpportunities ? (
          <div className="text-center py-10">
            <div className="w-16 h-16 bg-slate-100 rounded-full flex items-center justify-center mx-auto mb-4">
              <Briefcase className="w-8 h-8 text-slate-400" />
            </div>
            <h3 className="font-medium text-slate-900 mb-2">Você não está aberto a oportunidades</h3>
            <p className="text-sm text-slate-600 mb-4">
              Para ver vagas disponíveis, ative a opção "Aberto a novas oportunidades" no seu perfil.
            </p>
            <Button
              onClick={() => navigate('/m/profile/edit')}
              className="bg-cyan-600 hover:bg-cyan-700"
            >
              Atualizar Perfil
            </Button>
          </div>
        ) : loading ? (
          <div className="flex items-center justify-center py-20">
            <Loader2 className="w-8 h-8 animate-spin text-cyan-600" />
          </div>
        ) : filteredJobs.length === 0 ? (
          <div className="text-center py-20">
            <Briefcase className="w-12 h-12 text-slate-300 mx-auto mb-4" />
            <h3 className="font-medium text-slate-900 mb-2">Nenhuma vaga encontrada</h3>
            <p className="text-sm text-slate-600">
              {search || activeFiltersCount > 0 ? 'Tente ajustar os filtros' : 'Novas vagas em breve'}
            </p>
            {activeFiltersCount > 0 && (
              <button 
                onClick={clearFilters}
                className="text-cyan-600 font-medium mt-3"
              >
                Limpar filtros
              </button>
            )}
          </div>
        ) : (
          <div className="space-y-3">
            <p className="text-sm text-slate-600">{filteredJobs.length} vaga(s)</p>
            
            {filteredJobs.map((job) => {
              const isSaved = savedJobs.includes(job.id);
              const hasInclusao = job.inclusao_pcd || job.inclusao_50_plus;
              
              return (
                <Card 
                  key={job.id} 
                  className="border-slate-200 hover:shadow-md transition-shadow active:scale-[0.99]"
                  onClick={() => navigate(`/m/jobs/${job.id}`)}
                  data-testid={`mobile-job-${job.id}`}
                >
                  <CardContent className="p-4">
                    <div className="flex items-start gap-3">
                      <div className="w-12 h-12 bg-slate-100 rounded-xl flex items-center justify-center flex-shrink-0">
                        <Building2 className="w-6 h-6 text-slate-400" />
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2 mb-1 flex-wrap">
                          {job.destaque && (
                            <Badge className="bg-amber-100 text-amber-700 text-xs px-2">
                              <Star className="w-3 h-3 mr-1" />
                              Destaque
                            </Badge>
                          )}
                          <Badge variant="outline" className="text-xs">
                            {job.tipo_contrato}
                          </Badge>
                          {hasInclusao && (
                            <Badge className="bg-purple-100 text-purple-700 text-xs px-2">
                              <Users className="w-3 h-3 mr-1" />
                              Inclusão
                            </Badge>
                          )}
                        </div>
                        <h3 className="font-semibold text-slate-900 truncate">{job.titulo}</h3>
                        <p className="text-sm text-slate-600 truncate">{job.company_name}</p>
                        <div className="mt-1"><MatchBadge match={myMatches[job.id]} testid={`my-match-${job.id}`} /></div>
                        <div className="flex items-center gap-1 mt-1 text-xs text-slate-500">
                          <MapPin className="w-3 h-3" />
                          {job.localizacao}
                        </div>
                      </div>
                      
                      {/* Save Button */}
                      <button
                        onClick={(e) => handleSaveJob(job.id, e)}
                        className={`p-2 rounded-full transition-colors ${
                          isSaved 
                            ? 'bg-cyan-100 text-cyan-600' 
                            : 'bg-slate-100 text-slate-400 hover:bg-slate-200'
                        }`}
                        disabled={savingJob === job.id}
                      >
                        {savingJob === job.id ? (
                          <Loader2 className="w-5 h-5 animate-spin" />
                        ) : isSaved ? (
                          <BookmarkCheck className="w-5 h-5" />
                        ) : (
                          <Bookmark className="w-5 h-5" />
                        )}
                      </button>
                    </div>
                    
                    <Button 
                      className="w-full mt-3 bg-cyan-600 hover:bg-cyan-700"
                      size="sm"
                      onClick={(e) => handleApply(job.id, e)}
                      disabled={applying === job.id}
                    >
                      {applying === job.id ? (
                        <Loader2 className="w-4 h-4 animate-spin" />
                      ) : (
                        'Candidatar-se'
                      )}
                    </Button>
                  </CardContent>
                </Card>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
}
